
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from 'sonner';
import { 
  ShieldCheck,
  UserCheck,
  Key,
  Eye,
  Building
} from 'lucide-react';
import { useAdmin, SidebarFeature } from '@/contexts/AdminContext';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  Alert, 
  FormGroup, 
  FormControlLabel, 
  Checkbox 
} from '@mui/material';

export const AdminPanel = () => {
  const { t } = useTranslation();
  const { 
    impersonatingBusinessUnit, 
    setImpersonatingBusinessUnit,
    sidebarFeatures,
    updateSidebarFeature
  } = useAdmin();
  
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [masterPassword, setMasterPassword] = useState('');
  const [isImpersonating, setIsImpersonating] = useState(!!impersonatingBusinessUnit);

  // Group features for easier management in UI
  const featureGroups = [
    {
      title: t('settings.admin.coreFeatures'),
      features: sidebarFeatures.filter(f => 
        ['dashboard', 'recipes', 'settings', 'ai-assistant'].includes(f.id)
      )
    },
    {
      title: t('settings.admin.analyticsFeatures'),
      features: sidebarFeatures.filter(f => 
        ['menu-engineering', 'inventory-intelligence', 'financial-performance', 'reports'].includes(f.id)
      )
    },
    {
      title: t('settings.admin.operationalFeatures'),
      features: sidebarFeatures.filter(f => 
        ['supplier-relationship', 'dynamic-pricing', 'waste-reduction', 'procurement-calendar', 
         'recipe-innovation', 'operational-efficiency'].includes(f.id)
      )
    }
  ];

  // Handle impersonation
  const handleImpersonate = () => {
    if (!businessUnitId) {
      toast.error(t('settings.admin.enterBusinessId'));
      return;
    }

    if (!masterPassword) {
      toast.error(t('settings.admin.enterMasterPassword'));
      return;
    }

    // In a real app, you would verify the master password with your backend
    if (masterPassword !== 'admin123') { // Simple hardcoded check for demo
      toast.error(t('settings.admin.invalidMasterPassword'));
      return;
    }

    const businessId = parseInt(businessUnitId);
    setImpersonatingBusinessUnit(businessId);
    setIsImpersonating(true);
    toast.success(t('settings.admin.nowImpersonating', { id: businessId }));
  };

  // Handle stop impersonation
  const handleStopImpersonate = () => {
    setImpersonatingBusinessUnit(null);
    setIsImpersonating(false);
    setBusinessUnitId('');
    setMasterPassword('');
    toast.success(t('settings.admin.stoppedImpersonating'));
  };

  // Toggle sidebar feature
  const handleToggleFeature = (id: string, enabled: boolean) => {
    updateSidebarFeature(id, enabled);
    toast.success(
      enabled 
        ? t('settings.admin.featureEnabled', { feature: sidebarFeatures.find(f => f.id === id)?.name }) 
        : t('settings.admin.featureDisabled', { feature: sidebarFeatures.find(f => f.id === id)?.name })
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {t('settings.admin.adminPanel')}
          </CardTitle>
          <Badge variant="outline" className="bg-primary/10 text-primary">
            {t('settings.admin.adminAccess')}
          </Badge>
        </div>
        <CardDescription>
          {t('settings.admin.manageSiteFeatures')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Impersonation Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary" />
              {t('settings.admin.impersonation')}
            </h3>
            <Badge 
              variant={isImpersonating ? "destructive" : "outline"} 
              className={isImpersonating ? "" : "bg-muted/30"}
            >
              {isImpersonating ? t('settings.admin.active') : t('settings.admin.inactive')}
            </Badge>
          </div>
          
          <Separator />
          
          {isImpersonating ? (
            <div className="space-y-4">
              <Alert severity="warning">
                <Typography variant="body2">
                  {t('settings.admin.currentlyImpersonating', { id: impersonatingBusinessUnit })}
                </Typography>
              </Alert>
              
              <Button 
                variant="destructive" 
                className="w-full"
                onClick={handleStopImpersonate}
              >
                {t('settings.admin.stopImpersonating')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="businessUnitId">{t('settings.admin.businessUnitId')}</Label>
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="businessUnitId" 
                    value={businessUnitId}
                    onChange={(e) => setBusinessUnitId(e.target.value)}
                    placeholder="e.g. 1255"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="masterPassword">{t('settings.admin.masterPassword')}</Label>
                <div className="flex items-center space-x-2">
                  <Key className="h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="masterPassword" 
                    type="password"
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    placeholder="Enter master password"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Button 
                  className="w-full"
                  onClick={handleImpersonate}
                >
                  {t('settings.admin.impersonateRestaurant')}
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Feature Toggle Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              {t('settings.admin.featureVisibility')}
            </h3>
          </div>
          
          <Separator />
          
          <div className="space-y-6">
            {featureGroups.map((group, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{group.title}</Typography>
                <Grid container spacing={2}>
                  {group.features.map((feature) => (
                    <Grid item xs={12} sm={6} md={4} key={feature.id}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={feature.enabled}
                              onChange={(e) => handleToggleFeature(feature.id, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={feature.name}
                        />
                      </FormGroup>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
